import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { toast } from "react-toastify";
import {
  getPromotionById,
  updatePromotion,
  getPromotionSelectionData,
} from "../../api/promotion.api";

import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  EditorState,
  ContentState,
  convertToRaw,
} from "draft-js";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";

const PromotionEdit = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [selectionLoading, setSelectionLoading] = useState(false);

  const [selectionData, setSelectionData] = useState({
    packages: [],
    lots: [],
    addOns: [],
  });

  const [termsEditorState, setTermsEditorState] = useState(() =>
    EditorState.createEmpty()
  );

  const [form, setForm] = useState({
    promoCode: "",
    startDate: "",
    endDate: "",
    bannerImage: null,
    existingBannerImage: null,
    noExpiry: true,

    totalUsageLimit: "",
    perCustomerLimit: "",
    isStackable: false,

    discountType: "fixed_amount",
    discountValue: "",
    minimumSpend: "",
    discountCap: "",

    packageIds: [],
    lotIds: [],
    inventoryIds: [],

    packageMinQty: "",
    lotMinQty: "",
    inventoryMinQty: "",
  });

  const loadTermsEditor = (html = "") => {
    if (!html) return EditorState.createEmpty();

    const contentBlock = htmlToDraft(html);

    if (!contentBlock) {
      return EditorState.createEmpty();
    }

    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
      contentBlock.entityMap
    );

    return EditorState.createWithContent(contentState);
  };

  useEffect(() => {
    const init = async () => {
      try {
        setFetching(true);
        setSelectionLoading(true);

        const [selection, promotion] = await Promise.all([
          getPromotionSelectionData(),
          getPromotionById(id),
        ]);

        setSelectionData({
          packages: selection.packages || [],
          lots: selection.lots || [],
          addOns: selection.addOns || [],
        });

        const parseJsonField = (value, fallback = {}) => {
          if (!value) return fallback;

          if (typeof value === "string") {
            try {
              return JSON.parse(value);
            } catch {
              return fallback;
            }
          }

          return value;
        };

        const formula = parseJsonField(promotion.formula);
        const rules = parseJsonField(promotion.rules);
        const usage = rules.usage || {};
        const promotionItems = promotion.items || promotion.promotionItems || [];

        const packageItems = promotionItems.filter(
          (item) => item.itemType === "package"
        );

        const lotItems = promotionItems.filter(
          (item) => item.itemType === "lot"
        );

        const inventoryItems = promotionItems.filter(
          (item) => item.itemType === "add_on"
        );

        setForm({
          promoCode: promotion.promoCode || "",

          startDate: promotion.startDate || "",
          endDate: promotion.endDate || "",

          bannerImage: null,
          existingBannerImage: promotion.bannerImage || null,

          noExpiry:
            promotion.noExpiry !== undefined && promotion.noExpiry !== null
              ? Boolean(promotion.noExpiry)
              : !promotion.endDate,

          totalUsageLimit:
            usage.totalUsageLimit ?? "",

          perCustomerLimit:
            usage.perCustomerLimit ?? "",

          isStackable:
            usage.isStackable ?? false,

          discountType: formula.discountType || "fixed_amount",

          appliedBy: formula.appliedBy || "flat_fee",

          standardDiscountValue:
            formula.discountValue?.standard ?? "",

          peakDiscountValue:
            formula.discountValue?.peak ?? "",

          minimumSpend:
            formula.minimumSpend ?? "",

          discountCap:
            formula.discountCap ?? "",

          packageIds: packageItems.map((item) => Number(item.itemId)),
          lotIds: lotItems.map((item) => Number(item.itemId)),
          inventoryIds: inventoryItems.map((item) => Number(item.itemId)),

          packageMinQty:
            packageItems[0]?.minimumQuantity ?? 1,

          lotMinQty:
            lotItems[0]?.minimumQuantity ?? 1,

          inventoryMinQty:
            inventoryItems[0]?.minimumQuantity ?? 1,
        });

        setTermsEditorState(
          loadTermsEditor(promotion.termsAndConditions || "")
        );
      } catch (err) {
        toast.error(err.message);
      } finally {
        setFetching(false);
        setSelectionLoading(false);
      }
    };

    init();
  }, [id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onTermsEditorStateChange = (state) => {
    setTermsEditorState(state);
  };

  const handleIdToggle = (field, id, checked) => {
    setForm((prev) => ({
      ...prev,
      [field]: checked
        ? [...prev[field], id]
        : prev[field].filter((itemId) => itemId !== id),
    }));
  };

  const handleSelectAll = (field, items) => {
    setForm((prev) => ({
      ...prev,
      [field]: items.map((item) => item.id),
    }));
  };

  const handleClearAll = (field) => {
    setForm((prev) => ({ ...prev, [field]: [] }));
  };

  const lotsByLocation = selectionData.lots.reduce((acc, lot) => {
    const location = lot.location || "Others";
    if (!acc[location]) acc[location] = [];
    acc[location].push(lot);
    return acc;
  }, {});

  const isLocationSelected = (lots) =>
    lots.every((lot) => form.lotIds.includes(lot.id));

  const handleLocationToggle = (lots, checked) => {
    const lotIds = lots.map((lot) => lot.id);

    setForm((prev) => ({
      ...prev,
      lotIds: checked
        ? Array.from(new Set([...prev.lotIds, ...lotIds]))
        : prev.lotIds.filter((id) => !lotIds.includes(id)),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!form.promoCode) return toast.error("Promo code is required");
      if (!form.startDate) return toast.error("Effective date is required");
      if (!form.noExpiry && !form.endDate) {
        return toast.error("End date is required");
      }

      const termsHtml = draftToHtml(
        convertToRaw(termsEditorState.getCurrentContent())
      );

      const items = [
        ...form.packageIds.map((itemId) => ({
          itemType: "package",
          itemId,
          minimumQuantity: Number(form.packageMinQty || 1),
        })),

        ...form.lotIds.map((itemId) => ({
          itemType: "lot",
          itemId,
          minimumQuantity: Number(form.lotMinQty || 1),
        })),

        ...form.inventoryIds.map((itemId) => ({
          itemType: "add_on",
          itemId,
          minimumQuantity: Number(form.inventoryMinQty || 1),
        })),
      ];

      const payload = {
        promoCode: form.promoCode,
        noExpiry: form.noExpiry,
        startDate: form.startDate,
        endDate: form.noExpiry ? null : form.endDate,
        ...(form.bannerImage ? { bannerImage: form.bannerImage } : {}),
        termsAndConditions: termsHtml,

        formula: {
          minimumSpend: Number(form.minimumSpend || 0),
          discountType: form.discountType,
          appliedBy: form.appliedBy,
          discountValue: {
            standard: Number(form.standardDiscountValue || 0),
            peak: Number(form.peakDiscountValue || 0),
          },
          discountCap:
            form.discountType === "percentage"
              ? Number(form.discountCap || 0)
              : null,
        },
        items,
        rules: {
            usage: {
            totalUsageLimit: Number(form.totalUsageLimit || 0),
            perCustomerLimit: Number(form.perCustomerLimit || 0),
            isStackable: form.isStackable,
          },
        },
      };

      setLoading(true);

      await updatePromotion(id, payload);

      toast.success("Promotion updated successfully");
      navigate(`/promotions/view/${id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const triggerSubmit = () => {
    if (formRef.current) formRef.current.requestSubmit();
  };

  if (fetching) {
    return <div className="text-center mt-5">{titleCase(t("loading"))}...</div>;
  }

  const generatePromoCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";

    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    handleChange("promoCode", code);
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("promotions"))}
        title={titleCase(t("edit promotion"))}
        mainTitle={titleCase(t("edit promotion"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Form innerRef={formRef} onSubmit={handleSubmit}>
                          <Row className="mb-4">
                            {/* LEFT */}
                            <Col md="6">
                              <Card className="h-100">
                                <CardHeader>
                                  <H5>{titleCase(t("promotion details"))}</H5>
                                </CardHeader>
            
                                <CardBody>
                                  <FormGroup>
                                    <Label>{titleCase(t("promo code"))}</Label>
            
                                    <div className="d-flex gap-2">
                                      <Input
                                        type="text"
                                        value={form.promoCode}
                                        onChange={(e) =>
                                          handleChange("promoCode", e.target.value)
                                        }
                                        required
                                      />
            
                                      <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={generatePromoCode}
                                      >
                                        {titleCase(t("generate"))}
                                      </button>
                                    </div>
                                  </FormGroup>
            
                                  <FormGroup>
                                    <Label>{titleCase(t("banner image"))}</Label>

                                    {form.existingBannerImage && (
                                      <div className="mb-3">
                                        <img
                                          src={
                                            form.existingBannerImage?.startsWith("https")
                                              ? form.existingBannerImage
                                              : `${process.env.REACT_APP_API_URL}/${form.existingBannerImage}`
                                          }
                                          alt="promotion banner"
                                          style={{
                                            width: "100%",
                                            maxHeight: "180px",
                                            objectFit: "cover",
                                            borderRadius: "6px",
                                          }}
                                        />
                                      </div>
                                    )}
            
                                    <Input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) =>
                                        handleChange("bannerImage", e.target.files[0])
                                      }
                                    />
            
                                    <small className="text-muted d-block mt-2">
                                      {t(
                                        "Recommended dimensions: 1200 × 600 pixels."
                                      )}
                                    </small>
                                  </FormGroup>
                                </CardBody>
                              </Card>
                            </Col>
            
                            {/* RIGHT */}
                            <Col md="6">
                              <Card className="h-100">
                                <CardHeader>
                                  <H5>{titleCase(t("schedule"))}</H5>
                                </CardHeader>
            
                                <CardBody>
                                  <FormGroup>
                                    <Label>{titleCase(t("effective date"))}</Label>
            
                                    <Input
                                      type="date"
                                      value={form.startDate}
                                      onChange={(e) =>
                                        handleChange("startDate", e.target.value)
                                      }
                                      required
                                    />
                                  </FormGroup>
            
                                  <FormGroup>
                                    <Label className="d-block mb-2">
                                      {titleCase(t("Always Available"))}
                                    </Label>
            
                                    <div className="d-flex align-items-center gap-3">
                                      <span
                                        className={
                                          !form.noExpiry
                                            ? "fw-bold text-primary"
                                            : "text-muted"
                                        }
                                      >
                                        {titleCase(t("no"))}
                                      </span>
            
                                      <Label className="switch m-r-10 mb-0">
                                        <Input
                                          type="checkbox"
                                          checked={form.noExpiry}
                                          onChange={(e) => {
                                            const checked = e.target.checked;

                                            setForm((prev) => ({
                                              ...prev,
                                              noExpiry: checked,
                                              endDate: checked ? "" : prev.endDate,
                                            }));
                                          }}
                                        />
                                        <span className="switch-state"></span>
                                      </Label>
            
                                      <span
                                        className={
                                          form.noExpiry
                                            ? "fw-bold text-primary"
                                            : "text-muted"
                                        }
                                      >
                                        {titleCase(t("yes"))}
                                      </span>
                                    </div>
            
                                    <small className="text-muted d-block mt-2">
                                      {form.noExpiry
                                        ? t(
                                            "This promotion will remain active until manually disabled."
                                          )
                                        : t(
                                            "This promotion will automatically expire on the specified end date."
                                          )}
                                    </small>
                                  </FormGroup>
            
                                  {!form.noExpiry && (
                                    <FormGroup className="mb-0">
                                      <Label>{titleCase(t("end date"))}</Label>
            
                                      <Input
                                        type="date"
                                        value={form.endDate}
                                        onChange={(e) =>
                                          handleChange("endDate", e.target.value)
                                        }
                                        required
                                      />
            
                                      <small className="text-muted d-block mt-2">
                                        {t(
                                          "This promotion will automatically expire on the selected date."
                                        )}
                                      </small>
                                    </FormGroup>
                                  )}
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>
            
                          <Row className="mb-4">
                            {/* LEFT */}
                            <Col md="6">
                              <Card className="h-100">
                                <CardHeader>
                                  <H5>{titleCase(t("usage limits"))}</H5>
                                </CardHeader>
            
                                <CardBody>
                                  <Row>
                                    <Col md="6">
                                      <FormGroup>
                                        <Label>{titleCase(t("total redemption limit"))}</Label>
            
                                        <Input
                                          type="number"
                                          min="0"
                                          value={form.totalUsageLimit}
                                          onChange={(e) =>
                                            handleChange("totalUsageLimit", e.target.value)
                                          }
                                        />
            
                                        <small className="text-muted d-block mt-2">
                                          {form.totalUsageLimit
                                            ? `This promotion can be redeemed a maximum of ${form.totalUsageLimit} time(s) in total before it becomes unavailable.`
                                            : "Leave empty or set to 0 for unlimited redemptions."}
                                        </small>
                                      </FormGroup>
                                    </Col>
            
                                    <Col md="6">
                                      <FormGroup>
                                        <Label>{titleCase(t("redemption limit per customer"))}</Label>
                                        <Input
                                          type="number"
                                          min="0"
                                          value={form.perCustomerLimit}
                                          onChange={(e) =>
                                            handleChange("perCustomerLimit", e.target.value)
                                          }
                                        />
                                      </FormGroup>
                                    </Col>
                                  </Row>
            
                                  <FormGroup className="mb-0">
                                    <Label className="d-block mb-2">
                                      {titleCase(t("allow stacking with other promotions"))}
                                    </Label>
            
                                    <div className="d-flex align-items-center gap-3">
                                      <span
                                        className={
                                          !form.isStackable
                                            ? "fw-bold text-primary"
                                            : "text-muted"
                                        }
                                      >
                                        {titleCase(t("no"))}
                                      </span>
            
                                      <Label className="switch m-r-10 mb-0">
                                        <Input
                                          type="checkbox"
                                          checked={form.isStackable}
                                          onChange={(e) =>
                                            handleChange("isStackable", e.target.checked)
                                          }
                                        />
                                        <span className="switch-state"></span>
                                      </Label>
            
                                      <span
                                        className={
                                          form.isStackable
                                            ? "fw-bold text-primary"
                                            : "text-muted"
                                        }
                                      >
                                        {titleCase(t("yes"))}
                                      </span>
                                    </div>
            
                                    <small className="text-muted d-block mt-2">
                                      {form.isStackable
                                        ? t(
                                            "Customers may combine this promotion with other eligible promotions."
                                          )
                                        : t(
                                            "This promotion cannot be combined with any other promotion."
                                          )}
                                    </small>
                                  </FormGroup>
                                </CardBody>
                              </Card>
                            </Col>
            
                            {/* RIGHT */}
                            <Col md="6">
                              <Card className="h-100">
                                <CardHeader>
                                  <H5>{titleCase(t("discount configuration"))}</H5>
                                </CardHeader>
            
                                <CardBody>
                                  {/* Row 1 */}
                                  <Row>
                                    <Col md="6">
                                      <FormGroup>
                                        <Label>{titleCase(t("minimum eligible spend"))}</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={form.minimumSpend}
                                          onChange={(e) => handleChange("minimumSpend", e.target.value)}
                                        />
                                      </FormGroup>
                                    </Col>

                                    <Col md="6">
                                      <FormGroup>
                                        <Label>{titleCase(t("maximum discount cap"))}</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={form.discountCap}
                                          onChange={(e) => handleChange("discountCap", e.target.value)}
                                          disabled={form.discountType !== "percentage"}
                                          placeholder={
                                            form.discountType === "percentage"
                                              ? titleCase(t("maximum discount amount"))
                                              : titleCase(t("not applicable"))
                                          }
                                        />
                                      </FormGroup>
                                    </Col>
                                  </Row>

                                  {/* Row 2 */}
                                  <Row>
                                    <Col md="6">
                                      <FormGroup>
                                        <Label>{titleCase(t("discount type"))}</Label>
                                        <Input
                                          type="select"
                                          value={form.discountType}
                                          onChange={(e) => handleChange("discountType", e.target.value)}
                                        >
                                          <option value="fixed_amount">{titleCase(t("fixed amount"))}</option>
                                          <option value="percentage">{titleCase(t("percentage"))}</option>
                                        </Input>
                                      </FormGroup>
                                    </Col>

                                    <Col md="6">
                                      <FormGroup>
                                        <Label>{titleCase(t("applied by"))}</Label>
                                        <Input
                                          type="select"
                                          value={form.appliedBy}
                                          onChange={(e) => handleChange("appliedBy", e.target.value)}
                                        >
                                          <option value="hourly">Per Hour</option>
                                          <option value="nightly">Per Night</option>
                                          <option value="daily">Per Day</option>
                                          <option value="monthly">Per Month</option>
                                          <option value="yearly">Per Year</option>
                                          <option value="flat_fee">Flat Fee</option>
                                        </Input>
                                      </FormGroup>
                                    </Col>
                                  </Row>

                                  {/* Row 3 */}
                                  <Row>
                                    <Col md="6">
                                      <FormGroup>
                                        <Label>{titleCase(t("standard discount value"))}</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={form.standardDiscountValue}
                                          onChange={(e) => handleChange("standardDiscountValue", e.target.value)}
                                          required
                                        />
                                      </FormGroup>
                                    </Col>

                                    <Col md="6">
                                      <FormGroup>
                                        <Label>{titleCase(t("peak discount value"))}</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={form.peakDiscountValue}
                                          onChange={(e) => handleChange("peakDiscountValue", e.target.value)}
                                          required
                                        />
                                      </FormGroup>
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>
            
                          <Card className="mb-4">
                            <CardHeader>
                              <H5>{titleCase(t("applicability rules"))}</H5>
                            </CardHeader>
            
                            <CardBody>
                              {selectionLoading ? (
                                <p className="text-muted mb-0">
                                  {titleCase(t("loading"))}...
                                </p>
                              ) : (
                                <>
                                  <Row className="mb-4">
                                    <Col md="12">
                                      <div className="d-flex align-items-center gap-3 mb-3">
                                        <Label className="fw-bold mb-0">
                                          {titleCase(t("packages"))}
                                        </Label>
            
                                        <span
                                          className="text-primary"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleSelectAll(
                                              "packageIds",
                                              selectionData.packages
                                            )
                                          }
                                        >
                                          {titleCase(t("select all"))}
                                        </span>
            
                                        <span
                                          className="text-warning"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => handleClearAll("packageIds")}
                                        >
                                          {titleCase(t("clear all"))}
                                        </span>
                                      </div>
            
                                      <div className="d-flex flex-wrap gap-3">
                                        {selectionData.packages.map((item) => (
                                          <div
                                            key={item.id}
                                            className="checkbox checkbox-primary"
                                          >
                                            <Input
                                              id={`package-${item.id}`}
                                              type="checkbox"
                                              checked={form.packageIds.includes(item.id)}
                                              onChange={(e) =>
                                                handleIdToggle(
                                                  "packageIds",
                                                  item.id,
                                                  e.target.checked
                                                )
                                              }
                                            />
                                            <Label for={`package-${item.id}`}>
                                              {item.name}
                                            </Label>
                                          </div>
                                        ))}
                                      </div>
            
                                      <FormGroup className="mt-3">
                                        <Label>
                                          {titleCase(t("minimum package quantity"))}
                                        </Label>
                                        <Input
                                          type="number"
                                          min="1"
                                          value={form.packageMinQty}
                                          onChange={(e) =>
                                            handleChange("packageMinQty", e.target.value)
                                          }
                                        />
                                        <small className="text-muted d-block mt-2">
                                          {t(
                                            "Customers must include at least this number of eligible packages for the promotion to apply."
                                          )}
                                        </small>
                                      </FormGroup>
                                    </Col>
                                  </Row>
            
                                  <hr />
            
                                  <Row className="mb-4">
                                    <Col md="12">
                                      <div className="d-flex align-items-center gap-3 mb-3">
                                        <Label className="fw-bold mb-0">
                                          {titleCase(t("camp lots"))}
                                        </Label>
            
                                        <span
                                          className="text-primary"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleSelectAll(
                                              "lotIds",
                                              selectionData.lots
                                            )
                                          }
                                        >
                                          {titleCase(t("select all"))}
                                        </span>
            
                                        <span
                                          className="text-warning"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => handleClearAll("lotIds")}
                                        >
                                          {titleCase(t("clear all"))}
                                        </span>
                                      </div>
                                      
                                      <small className="text-muted d-block mb-4">
                                        {t(
                                          "Click a location name to quickly select or clear all camp lots within that location."
                                        )}
                                      </small>
            
                                      {Object.entries(lotsByLocation).map(([location, lots]) => (
                                        <div key={location} className="mb-4">
                                          <div
                                            className="fw-bold text-primary mb-1"
                                          >
                                            <span
                                              style={{ cursor: "pointer" }}
                                              onClick={() =>
                                                handleLocationToggle(lots, !isLocationSelected(lots))
                                              }
                                            >
                                              {location}
                                            </span>
                                          </div>
            
                                          <div className="d-flex flex-wrap gap-3 ms-3">
                                            {lots.map((item) => (
                                              <div key={item.id} className="checkbox checkbox-primary">
                                                <Input
                                                  id={`lot-${item.id}`}
                                                  type="checkbox"
                                                  checked={form.lotIds.includes(item.id)}
                                                  onChange={(e) =>
                                                    handleIdToggle("lotIds", item.id, e.target.checked)
                                                  }
                                                />
            
                                                <Label for={`lot-${item.id}`}>
                                                  {item.name}
                                                </Label>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
            
                                      <FormGroup className="mt-3">
                                        <Label>
                                          {titleCase(t("minimum camp lot quantity"))}
                                        </Label>
                                        <Input
                                          type="number"
                                          min="1"
                                          value={form.lotMinQty}
                                          onChange={(e) =>
                                            handleChange("lotMinQty", e.target.value)
                                          }
                                        />
                                        <small className="text-muted d-block mt-2">
                                          {t(
                                            "Customers must include at least this number of eligible camp lots for the promotion to apply."
                                          )}
                                        </small>
                                      </FormGroup>
                                    </Col>
                                  </Row>
            
                                  <hr />
            
                                  <Row>
                                    <Col md="12">
                                      <div className="d-flex align-items-center gap-3 mb-3">
                                        <Label className="fw-bold mb-0">
                                          {titleCase(t("inventory"))}
                                        </Label>
            
                                        <span
                                          className="text-primary"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleSelectAll(
                                              "inventoryIds",
                                              selectionData.addOns
                                            )
                                          }
                                        >
                                          {titleCase(t("select all"))}
                                        </span>
            
                                        <span
                                          className="text-warning"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleClearAll("inventoryIds")
                                          }
                                        >
                                          {titleCase(t("clear all"))}
                                        </span>
                                      </div>
            
                                      <div className="d-flex flex-wrap gap-3">
                                        {selectionData.addOns.map((item) => (
                                          <div
                                            key={item.id}
                                            className="checkbox checkbox-primary"
                                          >
                                            <Input
                                              id={`inventory-${item.id}`}
                                              type="checkbox"
                                              checked={form.inventoryIds.includes(item.id)}
                                              onChange={(e) =>
                                                handleIdToggle(
                                                  "inventoryIds",
                                                  item.id,
                                                  e.target.checked
                                                )
                                              }
                                            />
                                            <Label for={`inventory-${item.id}`}>
                                              {item.name}
                                            </Label>
                                          </div>
                                        ))}
                                      </div>
            
                                      <FormGroup className="mt-3">
                                        <Label>
                                          {titleCase(t("minimum inventory quantity"))}
                                        </Label>
                                        <Input
                                          type="number"
                                          min="1"
                                          value={form.inventoryMinQty}
                                          onChange={(e) =>
                                            handleChange(
                                              "inventoryMinQty",
                                              e.target.value
                                            )
                                          }
                                        />
                                        <small className="text-muted d-block mt-2">
                                          {t(
                                            "Customers must include at least this number of eligible inventory items for the promotion to apply."
                                          )}
                                        </small>
                                      </FormGroup>
                                    </Col>
                                  </Row>
                                </>
                              )}
                            </CardBody>
                          </Card>
            
                          <Card className="mb-4">
                            <CardHeader>
                              <H5>{titleCase(t("terms and conditions"))}</H5>
                            </CardHeader>
            
                            <CardBody>
                              <Editor
                                editorState={termsEditorState}
                                wrapperClassName="wrapperClassName"
                                editorClassName="editorClassName"
                                toolbarClassName="toolbarClassName"
                                onEditorStateChange={onTermsEditorStateChange}
                                wrapperStyle={{
                                  border: "solid 0.5px #d9d9d9",
                                  minHeight: "40vh",
                                }}
                                editorStyle={{
                                  paddingLeft: "15px",
                                  paddingRight: "15px",
                                }}
                              />
                            </CardBody>
                          </Card>
                        </Form>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={() => navigate(-1)}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default PromotionEdit;